import { PaginationState } from '@/models/pagination';
import { RootState, useAppDispatch } from '@/reducers';
import { PayloadAction } from '@reduxjs/toolkit';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { DEFAULT_PAGE_SIZE } from './constants';
import { TablePagination } from './TablePagination';
import { PaginatedQueryFn } from './types';

type TablePagerProps<T> = {
  stateSelector: (state: RootState) => PaginationState;
  stateUpdater: (pagination: PaginationState) => PayloadAction<PaginationState>;
  query: PaginatedQueryFn;
  queryResponse: T[] | undefined;
  setItems: (items: T[]) => void;
  querying: boolean;
  children: (pager: ReactNode) => ReactNode;
  totalCount: number;
  wrapperClassNames?: string;
};

export const TablePager = <T,>({ stateSelector, stateUpdater, query, queryResponse, setItems, querying, totalCount, ...props }: TablePagerProps<T>) => {
  const dispatch = useAppDispatch();
  const pagination = useSelector(stateSelector);

  const [pageNo, setPageNo] = useState<number>(pagination?.pageNo || 0);

  const queryFnRef = useRef<PaginatedQueryFn>(query);
  const pageNoRef = useRef<number>(pageNo);
  const pageSizeRef = useRef<number>(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    let actualPageNo: number = pageNo;

    if (queryFnRef.current !== query) {
      setPageNo(0);
      actualPageNo = 0;
      queryFnRef.current = query;
    }

    pageNoRef.current = actualPageNo;

    query(actualPageNo, DEFAULT_PAGE_SIZE);
  }, [query, pageNo]);

  useEffect(() => {
    if (queryResponse) {
      if (queryResponse.length || pageNoRef.current === 0) {
        setItems(queryResponse);
        dispatch(
          stateUpdater({
            pageNo: pageNoRef.current,
            pageSize: pageSizeRef.current,
          })
        );
      } else {
        setPageNo(pageNoRef.current - 1);
      }
    }
  }, [queryResponse]);

  return (
    <>
      {props.children(
        <div aria-disabled={querying} className={twMerge('mb-40 aria-disabled:pointer-events-none aria-disabled:opacity-50 w-full', props.wrapperClassNames)}>
          <TablePagination limit={DEFAULT_PAGE_SIZE} currentPage={pagination?.pageNo + 1} totalAmount={totalCount} onPageClick={setPageNo} />
        </div>
      )}
    </>
  );
};
